import * as React from "react";
import { Input, InputNumber, Popconfirm, Select, Switch, Checkbox, Form, FormItemProps, FormInstance } from "antd";
import { ColumnProps, TablePaginationConfig } from "antd/lib/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "./Button";
import { withTranslation, WithTranslation } from "react-i18next";
import i18n, { I18N_NS } from "Config/i18n";
import { numberSort } from "utils/defaultSort";
import { Table, TableProps } from "Components/General/Table";

interface EditableCellProps<T> extends ColumnProps<T> {
  inputType: editableCellInputType;
  inputProps: {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customInput?: React.ComponentType<any>;
  formItemProps?: FormItemProps;
  record: T;
  isOptional?: boolean;
  errorMessage?: string;
  editing: boolean;
  editable: boolean | "onlyNew";
  editingKey: string;
  subSelectOn?: EditableColumnSubSelectOn<T>[];
}

class EditableCell<T> extends React.Component<EditableCellProps<T>> {
  getInput = (record: T, props?: EditableColumnSubSelectOn<T>) => {
    const { inputType, inputProps, customInput: CustomInput } = props || this.props;

    if (inputType === "number") {
      return <InputNumber {...inputProps} />;
    } else if (inputType === "select") {
      return <Select size="small" {...inputProps} />;
    } else if (inputType === "switch") {
      return <Switch {...inputProps} />;
    } else if (inputType === "checkbox") {
      return <Checkbox {...inputProps} />;
    } else if (inputType === "custom" && CustomInput) {
      return <CustomInput {...inputProps} record={record} />;
    } else if (inputType === "color") {
      return <Input type="color" {...inputProps} />;
    } else {
      return <Input {...inputProps} />;
    }
  };

  renderSubSelect(editing: boolean) {
    const { record, isOptional, errorMessage, subSelectOn, editingKey } = this.props;

    const subSelect = subSelectOn?.find((val) => val.onParentValue === record["" + this.props.dataIndex]);
    if (!subSelect) {
      return null;
    }
    if (editing && subSelect.editable === "onlyNew" && editingKey !== NEW_ROW_ID) {
      editing = false;
    }
    const { dataIndex, formItemProps, title } = subSelect;
    if (subSelect.inputPropsFn) {
      subSelect.inputProps = subSelect.inputPropsFn(record);
    }
    if (!editing) {
      return subSelect.render ? subSelect.render(record["" + this.props.dataIndex], record, 0) : null;
    }
    let initialValue = [];
    if (record[`${dataIndex}`]) {
      initialValue = record[`${dataIndex}`]?.length ? [...record[`${dataIndex}`]] : [record[`${dataIndex}`]];
    }
    return (
      <Form.Item
        style={{ margin: 0 }}
        name={`${dataIndex}`}
        initialValue={initialValue}
        rules={
          isOptional
            ? []
            : [
                {
                  required: true,
                  message: errorMessage
                    ? errorMessage
                    : i18n.t(`{{title}} is required`, { title, ns: I18N_NS.PROJECT }),
                },
              ]
        }
        {...formItemProps}
      >
        {this.getInput(record, subSelect)}
      </Form.Item>
    );
  }

  render = () => {
    let editing = this.props.editing;
    const {
      editable,
      editingKey,
      dataIndex,
      title,
      inputType: _inputType,
      record,
      inputProps: _inputProps,
      formItemProps,
      isOptional,
      errorMessage,
      subSelectOn: _subSelectOn,
      editing: _editing,
      customInput: _customInput,
      render,
      ...restProps
    } = this.props;
    const tdStyle = editing ? { verticalAlign: "top", padding: 2 } : undefined;
    if (editing && editable === "onlyNew" && editingKey !== NEW_ROW_ID) {
      editing = false;
    }

    return (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      <td {...(restProps as any)} style={tdStyle}>
        {editing ? (
          <div>
            <Form.Item
              style={{ margin: 0 }}
              name={`${dataIndex}`}
              //   initialValue={record[`${dataIndex}`]}
              rules={
                isOptional
                  ? []
                  : [
                      {
                        required: true,
                        message: errorMessage
                          ? errorMessage
                          : i18n.t(`{{title}} is required`, { title, ns: I18N_NS.PROJECT }),
                      },
                    ]
              }
              {...formItemProps}
            >
              {this.getInput(record)}
            </Form.Item>
          </div>
        ) : render ? (
          render(record[`${dataIndex}`], record, 0)
        ) : (
          restProps.children
        )}
        {this.renderSubSelect(this.props.editing)}
      </td>
    );
  };
}

interface EditActionsProps extends WithTranslation {
  editable: boolean;
  deletable: boolean;
  moveable: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
  onDragStart: (ev: React.DragEvent) => void;
}

const EditActions = withTranslation(I18N_NS.PROJECT)(
  ({ t, editable, deletable, moveable, onEditClick, onDeleteClick, onDragStart }: EditActionsProps) => {
    return (
      <>
        {editable ? (
          <span style={{ cursor: "pointer" }} onClick={onEditClick}>
            <FontAwesomeIcon fixedWidth={true} icon={"edit"} />
          </span>
        ) : null}
        {deletable ? (
          <Popconfirm title={t("Are you sure?")} onConfirm={onDeleteClick}>
            <span style={{ cursor: "pointer", marginLeft: 5 }}>
              <FontAwesomeIcon fixedWidth={true} icon={["far", "trash-alt"]} />
            </span>
          </Popconfirm>
        ) : null}
        {moveable && (
          <span style={{ cursor: "row-resize" }} draggable={true} onDragStart={onDragStart}>
            <FontAwesomeIcon fixedWidth={true} icon={"arrow-up"} />
          </span>
        )}
      </>
    );
  }
);

type editableCellInputType = "text" | "number" | "select" | "switch" | "checkbox" | "custom" | "color";

export interface EditableColumnProps<T> extends ColumnProps<T> {
  editable: boolean | "onlyNew";
  inputType?: editableCellInputType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customInput?: React.ComponentType<any>;
  inputProps?: {};
  formItemProps?: FormItemProps;
  isOptional?: boolean;
  errorMessage?: string;
  subSelectOn?: EditableColumnSubSelectOn<T>[];
}

interface EditableColumnSubSelectOn<T> extends EditableColumnProps<T> {
  onParentValue: string;
  inputPropsFn?: (record: T) => {};
}

interface RowsMoveable<T> {
  onAfterMoveRow: (updatedData: T[]) => Promise<void> | void;
  positionField: keyof T;
}

interface EditableTableProps<T> extends TableProps<T> {
  columns: Array<EditableColumnProps<T>>;
  onEdit?: (values: {}, allValues: {}) => void | Promise<void>;
  rowIdKey: keyof T;
  showAddRow?: boolean;
  onAddRowButtonText?: string;
  onAddRow?: (values: {}) => Promise<void>;
  rowEditable?: boolean | ((r: T) => boolean);
  /* Boolean or function that receives record and should return boolean */
  rowDeletable?: boolean | ((r: T) => boolean);
  onDelete?: (id: string | number) => Promise<void> | void;
  rowsMoveable?: RowsMoveable<T>;
  ref?: React.Ref<EditableTableComponent<T>>;
}

interface EditableTableState {
  editingKey: string;
  withBlankRow: boolean;
  pagination: TablePaginationConfig;
  changedFormValues;
}

const NEW_ROW_ID = "_new";

export class EditableTableComponent<T extends Object> extends React.Component<
  EditableTableProps<T> & WithTranslation,
  EditableTableState
> {
  formRef = React.createRef<FormInstance>();
  draggingNode: HTMLElement;

  constructor(props: EditableTableProps<T> & WithTranslation) {
    super(props);
    this.state = {
      editingKey: "",
      withBlankRow: false,
      pagination: { hideOnSinglePage: true, pageSize: 10 },
      changedFormValues: null,
    };
  }

  isEditing = (record) => {
    return `${record[this.props.rowIdKey]}` === this.state.editingKey;
  };

  editRow = (editingKey: string) => {
    // additonal logic added cause DEV-8096
    const record = this.props.dataSource.find((item) => "" + item[this.props.rowIdKey as string] === editingKey);
    this.formRef.current.resetFields();
    const newRecord = {};
    this.props.columns.map((col) => {
      if (col.editable || col.subSelectOn) {
        newRecord[col.dataIndex as string] = record[col.dataIndex as string];
      }
      const subSelect = col.subSelectOn?.find((s) => s.onParentValue === record[col.dataIndex as string]);
      if (subSelect?.editable === "onlyNew") {
        newRecord[subSelect.dataIndex as string] = record[subSelect.dataIndex as string];
      }
    });
    this.formRef.current.setFieldsValue(newRecord);
    this.setState({ editingKey, changedFormValues: null });
  };

  save = async (id: string) => {
    try {
      const rowValues = await this.formRef.current?.validateFields();

      // if Save is pressed for new row - use onAddRow function
      if (this.state.editingKey === NEW_ROW_ID) {
        if (typeof this.props.onAddRow !== "function") {
          throw new Error('Set "onAddRow" function to be able to add new rows');
        }
        // extract temporary id from row values
        const { [this.props.rowIdKey]: _rowIdKey, ...values } = rowValues;

        await this.props.onAddRow(values);
      } else {
        const record = this.props.dataSource.find((item) => item[this.props.rowIdKey as string] === id);
        const values = { [this.props.rowIdKey]: id, ...(rowValues as Object) };
        const allValues = { ...record, ...rowValues };
        this.props.columns.forEach((col) => {
          if (col.editable === "onlyNew") {
            values["" + col.dataIndex] = record["" + col.dataIndex];
          }
          const subSelect = col.subSelectOn?.find((s) => s.onParentValue === record[col.dataIndex as string]);
          if (subSelect?.editable === "onlyNew") {
            values[subSelect.dataIndex as string] = record[subSelect.dataIndex as string];
          }
        });
        await this.props.onEdit(values, allValues);
      }
      this.cancel();
    } catch (error) {
      throw error;
    }
  };

  handlePageChange = (currentPage: number, pageSize: number) => {
    this.setState({
      ...this.state,
      pagination: {
        ...this.state.pagination,
        pageSize: pageSize,
        current: currentPage <= 0 ? 1 : currentPage,
        total: this.dataSource.length,
      },
    });
  };

  public resetPagination = ({ current, total }) => {
    this.setState({ pagination: { ...this.state.pagination, current: current, total: total } });
  };

  // Getter is used in order to handle update of column props.
  // For example, when select options are passed from MobX store that fetches data from back-end
  get columns() {
    const { t, rowDeletable, rowEditable = true, rowsMoveable } = this.props;
    return this.props.columns.concat([
      {
        dataIndex: this.props.rowIdKey as string,
        editable: false,
        width: 170,
        fixed: "right",
        render: (text, record) => {
          const editable = this.isEditing(record);
          const deletable = typeof rowDeletable === "function" ? rowDeletable(record) : rowDeletable;
          const isEditableRow = typeof rowEditable === "function" ? rowEditable(record) : rowEditable;
          return (
            <div>
              {editable ? (
                <span>
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      this.save(text);
                    }}
                    style={{ marginRight: 8 }}
                  >
                    {t("OK")}
                  </a>
                  <Popconfirm title={t("Are you sure?")} onConfirm={this.cancel}>
                    <a>{t("Abort")}</a>
                  </Popconfirm>
                </span>
              ) : (
                <EditActions
                  editable={isEditableRow}
                  deletable={deletable}
                  moveable={!!rowsMoveable}
                  onDragStart={this.onDragStart}
                  onEditClick={() => this.editRow(`${record[this.props.rowIdKey]}`)}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onDeleteClick={() => this.props.onDelete(record[this.props.rowIdKey] as any)}
                />
              )}
            </div>
          );
        },
      },
    ]);
  }

  cancel = () => {
    this.setState((prevState) => {
      const pageRatio = this.props.dataSource.length / this.state.pagination.pageSize;
      const getActivePageIndex = () => {
        // Just one page
        if (Math.trunc(pageRatio) === 0) {
          return 1;
        }
        // After adding new entries, if need a new page
        if (Math.trunc(pageRatio) > prevState.pagination.current) {
          return prevState.pagination.current + 1;
        }
        // After removing entries, if one page is redundant
        if (Math.trunc(pageRatio) < prevState.pagination.current) {
          return prevState.pagination.current + 1;
        }
        // The same page is active
        return prevState.pagination.current;
      };

      return {
        ...prevState,
        pagination: { ...prevState.pagination, current: getActivePageIndex(), total: this.props.dataSource.length },
        editingKey: "",
        withBlankRow: false,
        changedFormValues: null,
      };
    });
    this.formRef.current?.resetFields();
  };

  handleAddNewRow = () => {
    this.setState((prevState) => {
      const { pagination } = prevState;
      const totalPagesWithNewRow = this.props.dataSource.length + 1;
      let currentPage: number;
      if (totalPagesWithNewRow > pagination.pageSize) {
        currentPage = Math.ceil(totalPagesWithNewRow / pagination.pageSize);
      } else {
        currentPage = pagination.current;
      }
      return {
        ...prevState,
        pagination: { ...pagination, current: currentPage || 1, total: totalPagesWithNewRow },
        editingKey: NEW_ROW_ID,
        withBlankRow: true,
        changedFormValues: null,
      };
    });
  };

  get dataSource() {
    const { dataSource, rowsMoveable } = this.props;
    let ret = dataSource;
    if (this.props.rowsMoveable) {
      ret = (ret as []).sort((a, b) => numberSort(a[rowsMoveable.positionField], b[rowsMoveable.positionField]));
    }
    if (this.state.withBlankRow) {
      ret = dataSource.concat([{ [this.props.rowIdKey]: NEW_ROW_ID } as {} as T]);
    }
    return ret;
  }

  onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (
      this.draggingNode &&
      e.currentTarget instanceof HTMLElement &&
      e.currentTarget.dataset.rowKey &&
      this.draggingNode.dataset.rowKey !== e.currentTarget?.dataset.rowKey &&
      (this.draggingNode.previousSibling as HTMLElement)?.dataset.rowKey !== e.currentTarget?.dataset.rowKey
    ) {
      // setting the border bottom on td's, cause for some unkown reason it doesn't work on the tr
      Array.from(e.currentTarget.childNodes).forEach(
        (c) => ((c as HTMLElement).style.borderBottom = "1px dashed black")
      );
    }
  };

  onDragLeave = (e: React.DragEvent) => {
    Array.from(e.currentTarget.childNodes).forEach((c) => ((c as HTMLElement).style.borderBottom = ""));
  };

  onDragOverHeader = (e: React.DragEvent) => {
    if (this.draggingNode) {
      e.preventDefault();
      Array.from(this.draggingNode.parentNode.firstChild.childNodes).forEach(
        (c) => ((c as HTMLElement).style.borderTop = "1px dashed black")
      );
    }
  };

  onDragHeaderLeave = () => {
    Array.from(this.draggingNode.parentNode.firstChild.childNodes).forEach(
      (c) => ((c as HTMLElement).style.borderTop = "")
    );
  };

  onDragStart = (e: React.DragEvent) => {
    this.draggingNode = getParentByTagName(e.currentTarget, "TR");
  };

  onDrop = (e: React.DragEvent, droppedOnHeader?: boolean) => {
    if (droppedOnHeader) {
      this.onDragHeaderLeave();
      this.draggingNode.parentNode.insertBefore(this.draggingNode, this.draggingNode.parentNode.firstChild);
    } else {
      e.currentTarget.parentNode.insertBefore(this.draggingNode, e.currentTarget.nextSibling);
      this.onDragLeave(e);
    }

    const updatedData = this.dataSource.slice();
    let i = 1;
    for (let current = this.draggingNode.parentNode.firstChild; current; current = current.nextSibling) {
      const o = updatedData.find((o) => o["id"] + "" === (current as HTMLElement).dataset.rowKey);
      (o as unknown)[this.props.rowsMoveable.positionField] = i;
      i++;
    }
    this.props.rowsMoveable.onAfterMoveRow(updatedData);
  };

  onRow = (record: T, _rowIndex) => {
    if (this.props.rowsMoveable) {
      return {
        onClick: (event) => event.preventDefault(),
        onDragStart: this.onDragStart,
        onDragOver: this.onDragOver,
        onDragLeave: this.onDragLeave,
        onDrop: this.onDrop,
        positionKey: this.props.rowsMoveable ? record[this.props.rowsMoveable.positionField] : undefined,
      };
    }
    return {};
  };

  onHeaderRow = (_record, _rowIndex) => {
    if (this.props.rowsMoveable) {
      return {
        onDragOver: this.onDragOverHeader,
        onDragLeave: this.onDragHeaderLeave,
        onDrop: (e) => this.onDrop(e, true),
      };
    }
    return {};
  };

  handleFormChange = (changedValues, values) => {
    this.setState({ changedFormValues: values });
  };
  getEditRecord = (record) => {
    if (record.id !== this.state.editingKey) {
      return record;
    }
    return { ...record, ...this.state.changedFormValues };
  };

  render() {
    const { t, rowIdKey: _rowIdKey, rowsMoveable: _rowsMoveable, ref: _, ...restProps } = this.props;
    const components = {
      body: {
        cell: EditableCell,
      },
    };

    const columns = this.columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      const handleOnCell = (record: T): EditableCellProps<T> => ({
        record: this.getEditRecord(record),
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        inputProps: col.inputProps,
        isOptional: col.isOptional,
        errorMessage: col.errorMessage,
        customInput: col.customInput,
        formItemProps: col.formItemProps,
        title: col.title,
        editing: this.isEditing(record),
        editable: col.editable,
        editingKey: this.state.editingKey,
        subSelectOn: col.subSelectOn,
      });
      return {
        ...col,
        onCell: handleOnCell,
      };
    });

    return (
      <Form ref={this.formRef} component={false} onValuesChange={this.handleFormChange}>
        <Table<T>
          {...restProps}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dataSource={this.dataSource as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          components={components as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          columns={columns as any}
          onRow={this.onRow}
          onHeaderRow={this.onHeaderRow}
          rowClassName={() => `editable-row`}
          pagination={{
            ...this.state.pagination,
            onChange: this.handlePageChange,
          }}
          footer={
            this.props.showAddRow
              ? () => (
                  <Button onClick={this.handleAddNewRow} type="primary" faIcon={"plus"}>
                    {this.props?.onAddRowButtonText ? this.props.onAddRowButtonText : t("Add")}
                  </Button>
                )
              : null
          }
        />
      </Form>
    );
  }
}

function getParentByTagName(startElement, searchedTagName) {
  if (!startElement || !startElement.parentNode) return null;
  if (startElement.tagName === searchedTagName) return startElement;
  return getParentByTagName(startElement.parentNode, searchedTagName);
}

export const EditableTable = withTranslation(I18N_NS.PROJECT, { withRef: true })(EditableTableComponent) as <T = {}>(
  props: EditableTableProps<T>
) => React.ReactElement;
